define(["require", "exports", "tslib", "@arcgis/core/geometry/Polygon", "@arcgis/core/geometry/Polyline", "@arcgis/core/geometry/geometryEngine", "@arcgis/core/rest/geometryService", "@arcgis/core/rest/support/AreasAndLengthsParameters", "@arcgis/core/rest/support/BufferParameters", "@arcgis/core/rest/support/LengthsParameters", "./SpatialReferenceFactory"], function (require, exports, tslib_1, Polygon_1, Polyline_1, GeometryEngine, geometryService, AreasAndLengthsParameters_1, BufferParameters_1, LengthsParameters_1, SpatialReferenceFactory_1) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    exports.GeometryUtil = void 0;
    Polygon_1 = tslib_1.__importDefault(Polygon_1);
    Polyline_1 = tslib_1.__importDefault(Polyline_1);
    GeometryEngine = tslib_1.__importStar(GeometryEngine);
    geometryService = tslib_1.__importStar(geometryService);
    AreasAndLengthsParameters_1 = tslib_1.__importDefault(AreasAndLengthsParameters_1);
    BufferParameters_1 = tslib_1.__importDefault(BufferParameters_1);
    LengthsParameters_1 = tslib_1.__importDefault(LengthsParameters_1);
    const EARTH_CIRCUM = 2 * 3.14159265 * 6378137;
    class GeometryUtil {
        static getMaxAllowableOffset(map) {
            return (EARTH_CIRCUM / (256 * Math.pow(2, map.zoom))) / (EARTH_CIRCUM / 360);
        }
        static union(geometryServiceUrl, geometries) {
            return new Promise((resolve, reject) => {
                if (geometries.length > 0) {
                    if (geometries.length > 1) {
                        geometryService.union(geometryServiceUrl, geometries)
                            .then(geometry => {
                            if (geometry) {
                                resolve(geometry);
                            }
                            else {
                                reject(new Error("Geometry Service failed to union geometries"));
                            }
                        }).catch(reject);
                    }
                    else {
                        resolve(geometries[0]);
                    }
                }
                else {
                    reject(new Error("Unable to union empty geometry collection"));
                }
            });
        }
        static buffer(geometryServiceUrl, geometry, distance, unit) {
            const params = new BufferParameters_1.default();
            params.geometries = geometry;
            params.distances = distance;
            params.unit = unit == "m" ? "meters" : "kilometers";
            params.bufferSpatialReference = SpatialReferenceFactory_1.SpatialReferenceFactory.findOptimalProjectionFromGeographicCoordSystem(GeometryUtil.getCentroid(geometry[0]));
            params.outSpatialReference = SpatialReferenceFactory_1.SpatialReferenceFactory.GDA_1994;
            return new Promise((resolve, reject) => {
                geometryService.buffer(geometryServiceUrl, params).then(resolve).catch(reject);
            });
        }
        static generalize(x, maxDeviation, removeDegenerateParts, maxDeviationUnit) {
            return GeometryEngine.generalize(x, maxDeviation, removeDegenerateParts, maxDeviationUnit);
        }
        static simplify(x) {
            return GeometryEngine.simplify(x);
        }
        static disjoint(a, b) {
            return GeometryEngine.disjoint(a, b);
        }
        static distance(a, b, unit = null) {
            return GeometryEngine.distance(a, b, unit);
        }
        static closestPolylinePoint(a, b, unit = null) {
            let shortestDistance = 1000000;
            let closest = null;
            for (let o = 0; o < a.paths.length; o++) {
                for (let i = 0; i < a.paths[o].length; i++) {
                    const point = a.getPoint(o, i);
                    const distance = GeometryEngine.distance(point, b, unit);
                    if (distance < shortestDistance) {
                        shortestDistance = distance;
                        closest = point;
                    }
                }
            }
            return { point: closest, distance: shortestDistance };
        }
        static areEqual(a, b) {
            return GeometryEngine.equals(a, b);
        }
        static getCentroid(x) {
            return x instanceof Polygon_1.default || x instanceof Polyline_1.default ? x.extent.center : x;
        }
        static getPolylineStartPoint(polyline) {
            return polyline.getPoint(0, 0);
        }
        static setPolylineStartPoint(polyline, point) {
            polyline.setPoint(0, 0, point);
        }
        static getPolylineEndPoint(polyline) {
            const lastPathIndex = polyline.paths.length - 1;
            const lastPathLastPointIndex = polyline.paths[lastPathIndex].length - 1;
            return polyline.getPoint(lastPathIndex, lastPathLastPointIndex);
        }
        static setPolylineEndPoint(polyline, point) {
            const lastPathIndex = polyline.paths.length - 1;
            const lastPathLastPointIndex = polyline.paths[lastPathIndex].length - 1;
            polyline.setPoint(lastPathIndex, lastPathLastPointIndex, point);
        }
        static measureLength(geometryServiceUrl, g, unit = "meters") {
            return new Promise((resolve, reject) => {
                if (g) {
                    let p = new LengthsParameters_1.default();
                    p.polylines = [g];
                    p.lengthUnit = unit == "meters" ? 9001 : 9036;
                    p.geodesic = true;
                    geometryService.lengths(geometryServiceUrl, p)
                        .then(res => {
                        if (res && res.lengths.length > 0) {
                            resolve(res.lengths[0]);
                        }
                        else {
                            reject(new Error("measureLength failed"));
                        }
                    }).catch(reject);
                }
                else {
                    reject(new Error("Invalid input"));
                }
            });
        }
        static measureArea(geometryServiceUrl, g, areaUnit = "acres", lengthUnit = "meters") {
            return new Promise((resolve, reject) => {
                if (g) {
                    let q = new AreasAndLengthsParameters_1.default();
                    q.polygons = [g];
                    q.lengthUnit = lengthUnit;
                    q.areaUnit = areaUnit;
                    geometryService.areasAndLengths(geometryServiceUrl, q)
                        .then((res) => {
                        if (res) {
                            resolve({ area: res.areas[0], length: res.lengths[0] });
                        }
                        else {
                            reject(new Error("measureArea failed"));
                        }
                    }).catch(reject);
                }
                else {
                    reject(new Error("Invalid input"));
                }
            });
        }
        static mergePolylineToSinglePath(polyline) {
            const originalPaths = polyline.paths;
            if (originalPaths.length <= 1) {
                return polyline.clone();
            }
            let mergedPath = [];
            for (let i = 0; i < originalPaths.length; i++) {
                const currentPath = originalPaths[i];
                if (mergedPath.length === 0) {
                    mergedPath = [...currentPath];
                }
                else {
                    const lastMergedPoint = mergedPath[mergedPath.length - 1];
                    const firstCurrentPoint = currentPath[0];
                    const isDuplicate = lastMergedPoint[0] === firstCurrentPoint[0] &&
                        lastMergedPoint[1] === firstCurrentPoint[1];
                    mergedPath = isDuplicate
                        ? mergedPath.concat(currentPath.slice(1))
                        : mergedPath.concat(currentPath);
                }
            }
            return new Polyline_1.default({
                spatialReference: polyline.spatialReference,
                paths: [mergedPath]
            });
        }
    }
    exports.GeometryUtil = GeometryUtil;
});
